import { useEffect, useState } from 'react';
import intl from 'i18n/intl';
import _ from 'lodash';
import {
  fetchEmailPreferences,
  updateEmailPreferences,
  updatedEmailPreferencesSelector,
} from 'slices/emailPreferences.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import type { TEmailPreferences } from '@timeedit/types';
import { Button, DefaultContentLayout, TEInput } from '@timeedit/ui-components';
import { Typography } from '@timeedit/ui-components/antd';
import './index.scss';

const language: any = intl.messages;
const typeOptions = [
  { value: 'res', label: 'Reserved e-mail' },
  { value: 'can', label: 'Cancelled e-mail' },
  { value: 'req', label: 'Requested e-mail' },
  { value: 'rej', label: 'Rejected e-mail' },
  { value: 'acc', label: 'Accepted e-mail' },
  { value: 'crq', label: 'Cancel requested e-mail' },
];

const EmailSettings = () => {
  const dispatch = useAppDispatch();
  const [currentTemplate, setCurrentTemplate] = useState<string>('');
  const [emailTemplates, setEmailTemplates] = useState<
    Partial<TEmailPreferences>
  >({});
  const updatedEmailPreferences = useAppSelector(
    updatedEmailPreferencesSelector
  );
  /**
   * EFFECTS
   */
  useEffect(() => {
    dispatch(fetchEmailPreferences());
  }, []);
  useEffect(() => {
    if (updatedEmailPreferences && updatedEmailPreferences.emailTemplates) {
      setEmailTemplates(_.keyBy(updatedEmailPreferences.emailTemplates, 'id'));
    }
  }, [updatedEmailPreferences]);
  /**
   * EVENT HANDLERS
   */
  const onSaveChanges = () => {
    dispatch(
      updateEmailPreferences({
        ...updatedEmailPreferences,
        emailTemplates: Object.values(emailTemplates),
      })
    );
  };

  const onDiscardChanges = () => {
    setEmailTemplates(_.keyBy(updatedEmailPreferences.emailTemplates, 'id'));
  };

  return (
    <DefaultContentLayout
      title={language.email_templates}
      subtitle={language.email_templates_setting}
      actions={
        <>
          <Button onClick={onDiscardChanges}>
            {language.discard_changes as string}
          </Button>
          <Button type="primary" onClick={onSaveChanges}>
            {language.save_changes as string}
          </Button>
        </>
      }
    >
      <div className="email-organization-page" data-testid="organization-page">
        <div className="email-content">
          <div className="header-item">
            <Typography.Title level={4}>
              {language.email_templates}
            </Typography.Title>
          </div>
          <div className="label-item">
            <TEInput.Select
              label={language.email_type}
              tooltip={language.email_type}
              placeholder="Email preferences"
              onChange={setCurrentTemplate}
              options={typeOptions}
            />
          </div>
          <div className="label-item">
            <TEInput.Input
              label={language.template}
              tooltip={language.template}
              flow="column"
              placeholder="Subject"
              value={emailTemplates[currentTemplate]?.subject}
              onChange={(val) =>
                setEmailTemplates({
                  ...emailTemplates,
                  [currentTemplate]: {
                    ...emailTemplates[currentTemplate],
                    subject: val,
                  },
                })
              }
            />
          </div>

          <div className="label-item">
            <TEInput.TextArea
              className="textarea"
              data-testid="comments"
              value={emailTemplates[currentTemplate]?.message}
              onChange={(val) =>
                setEmailTemplates({
                  ...emailTemplates,
                  [currentTemplate]: {
                    ...emailTemplates[currentTemplate],
                    message: val,
                  },
                })
              }
            />
          </div>
          <div className="label-item te-mt-2">
            <TEInput.TextArea
              label={`Variables (to use in email)`}
              tooltip={`Variables (to use in email)`}
              disabled
              flow="column"
              className="textarea"
              value={`$database_name
$database_signature

$date
$date_long
$date_short

$reservation_count

$server_name
$server_signature

$time
$time_long

$user
$user_email
$user_first_name
$user_last_name
$user_name
$user_timezone
              `}
            />
          </div>
        </div>
      </div>
    </DefaultContentLayout>
  );
};

export default EmailSettings;
